import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import { FormInputs, LineItemNotes, ProductSelectionControls } from './common';
import PrintsFormInputs from './Prints';
import { FramingFormInputs } from './FramedPrints';
import CircleOvalFramingFormInputs from './CircleOvalFrames';
import MattedPrintsFormInputs from './MattedPrints';
import { AlbumFormInputs } from './Albums';
import CanvasFormInputs from './Canvas';
import ArtistProofsFormInputs from './ArtistProofs';
import { MultiOpeningMatFormInputs } from './MultiOpening';
import ImagesUpload from '../PrintOrder/PrintOrderCreate/ImagesUpload';
import styles from '../../pages/ProductSelection.module.css';
import { getAlbumLineItemPhotoValidationError, getAlbumSpreadError } from '../../lib/utils';
import useProductSelectionStore from '../../stores/useProductSelectionStore';
import useAlbumSelectionStore from '../../stores/useAlbumSelectionStore';
import useMultiOpeningMatStore from '../../stores/useMultiOpeningMatStore';
import { PRODUCT_TYPE_OPTIONS } from '../../lib/const';
import MountedPrintsFormInputs from './MountedPrints';
import TestPrintsFormInputs from './TestPrints';
import { useConfirmationModal } from '../../hooks';
import CustomTooltip from '../common/Tooltip';

const ProductSelectionSidebar = ({ orderId, lineItemId, quantityRef, queryClient }) => {
  const [resetMessage, setResetMessage] = useState('Are you sure you want to delete the thumbnail tray photos');
  const { Modal, setIsOpen } = useConfirmationModal();
  const navigate = useNavigate();
  const { productType, subtotal, cartCount, form, setForm, setSubtotal, handleAddToCart, lineItem, setIsEditInitializing, isEditInitializing, resetFormInputs, getMetaResets, handleUploadPhoto, photos, isResetting, handleResetPhotos, setModalIsOpen, setProductType, resetSelectedOptions, photosLoadingMessage, addToCart } = useProductSelectionStore();
  
  const {
    albumForm,
    setAlbumForm,
    getAlbumForm,
    calculateAlbumSubtotal,
    getAlbumMissingRequiredSelectionLabels,
    albumItems,
  } = useAlbumSelectionStore();
  const { calculateSubtotal: calculateMultiOpeningSubtotal, form: multiOpeningForm, resetForm: resetMultiOpeningForm } = useMultiOpeningMatStore();
  const initialMappingDone = useRef(false);
  const originalLineItemRef = useRef(null);
  const previousProductType = useRef(productType);
  const isInitialMount = useRef(true);
  const resettablePhotos = photos.filter(
    (photo) => !photo.attributes?.inCart && !photo.attributes?.isAddedToCart,
  );

  useEffect(() => {
    if (lineItem && lineItem !== originalLineItemRef.current) {
      originalLineItemRef.current = lineItem;
      initialMappingDone.current = false;
      setIsEditInitializing(true);
    } else if (!lineItem) {
      initialMappingDone.current = false;
      originalLineItemRef.current = null;
      setIsEditInitializing(false);
    }
  }, [lineItem, setIsEditInitializing]);

  // Update review order error based on required inputs
  useEffect(() => {
    if (productType === 'albums') {
      setForm((prevState) => {
        if (!prevState?.meta?.reviewOrderError) return prevState
        return {
          ...prevState,
          meta: { ...prevState.meta, reviewOrderError: undefined },
        }
      })
      return
    }
    if (!form) return
    setForm((prevState) => {
      if (!prevState) return prevState
      const newState = { ...prevState }
      if (newState.meta.requiredInputs === undefined || newState.meta.requiredInputs.length === 0) {
        newState.meta.reviewOrderError = undefined
      } else {
        newState.meta.reviewOrderError = lineItemId
          ? `To update this item, please select ${prevState.meta.requiredInputs.join(', ')}.`
          : `To add this item to your cart, please select ${prevState.meta.requiredInputs.join(', ')}.`
      }
      return newState
    })
  }, [form?.meta?.requiredInputs, productType, lineItemId, setForm])

  useEffect(() => {
    if (productType !== 'albums' || !albumForm) {
    setAlbumForm((prevState) => {
      if (!prevState?.meta?.reviewOrderError) return prevState;
      return{
        ...prevState,
        meta: { ...prevState.meta, reviewOrderError: undefined },
      }
    })
    return;
  }
    if (!albumForm) return;
    const missing = getAlbumMissingRequiredSelectionLabels();
    const orderedPhotos = albumItems?.length > 0 ? albumItems : photos;
    const photoError = getAlbumLineItemPhotoValidationError(orderedPhotos, Boolean(lineItemId));
    const spreadError = getAlbumSpreadError(albumItems, albumForm);
    setAlbumForm((prevState) => {
      if (!prevState) return prevState;
      const nextError =
        missing.length > 0
          ? lineItemId
            ? `To update this item, please select ${missing.join(', ')}.`
            : `To add this item to your cart, please select ${missing.join(', ')}.`
          : photoError ?? spreadError;
      if (prevState.meta?.reviewOrderError === nextError) return prevState;
      return {
        ...prevState,
        meta: { ...prevState.meta, reviewOrderError: nextError },
      };
    });
  }, [
    productType,
    albumForm,
    photos,
    albumItems,
    setAlbumForm,
    getAlbumMissingRequiredSelectionLabels,
    lineItemId,
  ]);

  // Subtotal value configuration
  useEffect(() => {
    if (productType === 'albums') {
      const albumSubtotal = calculateAlbumSubtotal();
      setSubtotal(albumSubtotal);
      return;
    }

    if (productType === 'multi-opening') {
      setSubtotal(calculateMultiOpeningSubtotal());
      return;
    }

    if (!form || !form.inputs) return
    // Skip recalculation if we're editing and printSize price isn't set yet
    const hasPriceSet = form.inputs.printSize?.selectedValuePrice ||
      form.inputs.matSize?.selectedValuePrice ||
      form.inputs.mountColor?.selectedValuePrice ||
      form.inputs.acrylic?.selectedValuePrice

    if (!hasPriceSet && subtotal > 0 && isEditInitializing) return;

    const inputs = [
      form.inputs.printSize?.selectedValuePrice,
      form.inputs.mountColor?.selectedValuePrice,
      form.inputs.matSize?.selectedValuePrice,
      form.inputs.frameType?.selectedValuePrice,
      form.inputs.frame?.selectedValuePrice,
      form.inputs.frameMat?.selectedValuePrice,
      form.inputs.acrylic?.selectedValuePrice,
      form.inputs.frameStain?.selectedValuePrice,
    ]
    let sum = 0

    inputs.forEach((input) => {
      const floatValue = parseFloat(input)
      if (!Number.isNaN(floatValue)) {
        sum = Math.max(sum, floatValue)
      }
    })
    const acrylicPrice = parseFloat(form.inputs.acrylic?.selectedValuePrice)
    if (acrylicPrice) {
      setSubtotal(acrylicPrice.toFixed(2))
    } else {
      setSubtotal(sum.toFixed(2))
    }
  }, [
    productType,
    form?.inputs?.printSize?.selectedValuePrice,
    form?.inputs?.mountColor?.selectedValuePrice,
    form?.inputs?.matSize?.selectedValuePrice,
    form?.inputs?.frameType?.selectedValuePrice,
    form?.inputs?.frame?.selectedValuePrice,
    form?.inputs?.frameMat?.selectedValuePrice,
    form?.inputs?.acrylic?.selectedValuePrice,
    form?.inputs?.frameStain?.selectedValuePrice,
    albumForm?.inputs?.albumSize?.selectedValuePrice,
    albumForm?.inputs?.albumSize?.selectedValue,
    albumForm?.inputs?.numberOfSpreads?.selectedValue,
    albumForm?.inputs?.debossingType?.selectedValue,
    albumForm?.inputs?.debossingType?.selectedValuePrice,
    albumForm?.inputs?.clamshellUpgrade?.selectedValue,
    albumForm?.inputs?.clamshellDebossingType?.selectedValue,
    albumForm?.inputs?.clamshellDebossingType?.selectedValuePrice,
    calculateAlbumSubtotal,
    calculateMultiOpeningSubtotal,
    multiOpeningForm?.inputs?.printSize?.selectedValuePrice,
    multiOpeningForm?.inputs?.acrylic?.selectedValuePrice,
    multiOpeningForm?.inputs?.matType?.selectedValuePrice,
  ])

  useEffect(() => {

    if (lineItemId) {
      previousProductType.current = productType;
      return;
    }

    if (isInitialMount.current) {
      isInitialMount.current = false;
      previousProductType.current = productType;
      return;
    }

    if (previousProductType.current === productType) {
      return;
    }

    const isChangingBetweenTypes = 
      previousProductType.current !== 'default' && 
      productType !== 'default' && 
      previousProductType.current !== productType;

    if (!isChangingBetweenTypes) {
      previousProductType.current = productType;
      return;
    }

    if (lineItem && !initialMappingDone.current) {
      initialMappingDone.current = true;
      setIsEditInitializing(false);
    }

    setForm((prevState) => {
      const newInputs = resetFormInputs(prevState.inputs || {}, null)
      
      return {
        meta: {
          ...prevState.meta,
          finishingCategoryId: undefined,
          finishingServiceId: undefined,
          finishingDetailId: undefined,
        },
        inputs: newInputs,
      }
    })

    previousProductType.current = productType;
  }, [productType, lineItem, lineItemId, setIsEditInitializing, setForm, resetFormInputs])

  // Initialize album form when productType changes to albums
  useEffect(() => {
    if (productType === 'albums' && !lineItemId) {
      const initialAlbumForm = getAlbumForm();
      setAlbumForm(initialAlbumForm);
    }
  }, [productType, lineItemId, getAlbumForm, setAlbumForm]);

  // Handler for text input changes
  const handleTextInputChange = (key, value) => {
    setForm((prevState) => ({
      ...prevState,
      inputs: {
        ...prevState.inputs,
        [key]: {
          ...prevState.inputs[key],
          selectedValue: value,
        },
      },
    }));
  };

  // Handler for form input changes
  const handleInputChange = (key, e) => {
    const input = form?.inputs?.[key];

    if (lineItem && !initialMappingDone.current) {
      initialMappingDone.current = true;
      setIsEditInitializing(false);
    }

    // If input doesn't exist, skip
    if (!input) return;

    // Handle "none" value for acrylic
    if (key === 'acrylic' && e.target.value === 'none') {
      setForm((prevState) => ({
        ...prevState,
        meta: {
          ...prevState.meta,
          finishingServiceId: prevState.inputs?.frame?.selectedValue || prevState.meta.finishingServiceId,
          finishingDetailId: prevState.inputs?.frameStain?.selectedValue || prevState.meta.finishingDetailId,
          requiredInputs: [
            ...(prevState.inputs?.frameType?.selectedValueText === 'Hardwood Frame' ? ['Frame Stain'] : [])
          ],
        },
        inputs: {
          ...prevState.inputs,
          acrylic: {
            ...prevState.inputs.acrylic,
            selectedValue: 'none',
            selectedValueText: 'None',
            selectedValuePrice: 0,
          },
          ...(prevState.inputs?.frameType?.selectedValueText === 'Hardwood Frame' && { frameStain: prevState.inputs.frameStain }),
        },
      }))
      return
    }
    // Handle "none" value for frameMat
    if (key === 'frameMat' && e.target.value === 'none') {
      setForm((prevState) => ({
        ...prevState,
        inputs: {
          ...prevState.inputs,
          frameMat: {
            ...prevState.inputs.frameMat,
            selectedValue: 'none',
            selectedValueText: 'None',
          },
        },
      }))
      return
    }
    // Handle "none" value for frameStain
    if (key === 'frameStain' && e.target.value === 'none') {
      setForm((prevState) => ({
        ...prevState,
        meta: {
          ...prevState.meta,
          finishingServiceId: prevState.inputs?.frame?.selectedValue || prevState.inputs?.acrylic?.selectedValue || prevState.meta.finishingServiceId,
          finishingDetailId: undefined,
          requiredInputs: [],
        },
        inputs: {
          ...prevState.inputs,
          frameStain: {
            ...prevState.inputs.frameStain,
            selectedValue: 'none',
            selectedValueText: 'None',
          },
        },
      }))
      return
    }
    // Default handling
    setForm((prevState) => {
     
      const resetInputs = resetFormInputs(prevState.inputs, key)
  
      resetInputs[key] = {
        ...resetInputs[key],
        selectedValue: e.target.value,
        selectedValueText: e.target.options[e.target.selectedIndex].text,
        selectedValuePrice: input.options.find(
          (option) => option?.value?.toString() === e.target.value,
        )?.price,
      }

      // Reset meta IDs based on which input changed (pass current form state)
      const newMeta = { ...prevState.meta }
      const metaResets = getMetaResets(key, prevState)
      
      if (metaResets.finishingCategoryId) {
        newMeta.finishingCategoryId = undefined
      }
      if (metaResets.finishingServiceId) {
        newMeta.finishingServiceId = undefined
      }
      if (metaResets.finishingDetailId) {
        newMeta.finishingDetailId = undefined
      }

      if (input.options[0]?.xAspectRatio && input.options[0]?.yAspectRatio) {
        const selectedOption = input.options.find(
          (option) => option?.value?.toString() === e.target.value,
        )
        newMeta.xAspectRatio = selectedOption?.xAspectRatio
        newMeta.yAspectRatio = selectedOption?.yAspectRatio
        newMeta.aspectRatio = selectedOption?.xAspectRatio / selectedOption?.yAspectRatio
      }

      return {
        meta: newMeta,
        inputs: resetInputs,
      }
    })
  }

  return (
    <div className="sidebar-product-selection white">
      <ProductSelectionControls orderId={orderId} />
      
      <div className="print-order-sidebar grid">
        <div className="accordion-item-block">
          <div className={styles.productCategoryLabel}>PRODUCT CATEGORY</div>
          <div className={styles.productCategoryHeader}>
            <h2 className={styles.productCategoryValue}>
              {PRODUCT_TYPE_OPTIONS.find((opt) => opt.value === productType)?.text || productType}
            </h2>
            {!lineItemId && productType !== 'albums' && (
              <CustomTooltip title="Reset All Photos">
                <button
                  type="button"
                  className="div-block-169-copy ghost"
                  style={{
                    width: 'auto',
                    height: '32px',
                    minHeight: '32px',
                    padding: '0 10px',
                    fontSize: '12px',
                  }}
                  onClick={() => {
                    if (!photos.length) return;
                    setResetMessage('Are you sure you want to delete the thumbnail tray photos');
                    setIsOpen(true);
                  }}
                  disabled={isResetting}
                >
                  {isResetting ? 'resetting...' : 'Reset'}
                </button>
              </CustomTooltip>
            )}
            {/* {!lineItemId && (
              <button
                type="button"
                className={styles.changeButton}
                onClick={() => {
                  if (productType === 'multi-opening') resetMultiOpeningForm();
                  setProductType('default')
                  resetSelectedOptions()
                }}
              >
                Change
              </button>
            )} */}
          </div>

          <Modal
            message={resetMessage}
            onContinue={() => {
              if (!resettablePhotos.length) {
                setResetMessage('Photographs added to the cart cannot be deleted');
                return;
              }
              setIsOpen(false);
              handleResetPhotos(orderId, productType, queryClient);
            }}
          />

          {(
            productType === 'prints' || productType === 'canvas' ||
            productType === 'albums' || productType === 'framed-prints' ||
            productType === 'matted-prints' || productType === 'circle-oval' ||
            productType === 'mounted-prints' || productType === 'artist-proofs' ||
            productType === 'test-prints' || productType === 'multi-opening'
          ) && (
            <>
              {productType !== 'albums' && photos.length === 0 && (
                <ImagesUpload disabled={false} uploading={!!photosLoadingMessage} onUpload={(files) => handleUploadPhoto(files, orderId, queryClient)} productType={productType} />
              )}

              {productType === 'prints' ? (
                <PrintsFormInputs quantityRef={quantityRef} />
              ) : productType === 'framed-prints' ? (
                <FramingFormInputs quantityRef={quantityRef} />
              ) : productType === 'canvas' ? (
                <CanvasFormInputs quantityRef={quantityRef} />
              ) : productType === 'circle-oval' ? (
                <CircleOvalFramingFormInputs quantityRef={quantityRef} />
              ) : productType === 'matted-prints' ? (
                <MattedPrintsFormInputs quantityRef={quantityRef} />
              ) : productType === 'albums' ? (
                <AlbumFormInputs />
              ) : productType === 'mounted-prints' ? (
                <MountedPrintsFormInputs quantityRef={quantityRef} />
              ) : productType === 'test-prints' ? (
                <TestPrintsFormInputs quantityRef={quantityRef} />
              ) : productType === 'artist-proofs' ? (
                <ArtistProofsFormInputs quantityRef={quantityRef} />
              ) : productType === 'multi-opening' ? (
                <MultiOpeningMatFormInputs quantityRef={quantityRef} />
              ) : (
                <FormInputs
                  quantityRef={quantityRef}
                  onInputChange={handleInputChange}
                  onTextInputChange={handleTextInputChange}
                  orderId={orderId}
                  lineItemId={lineItemId}
                />
              )}
            </>
          )}
        </div>

        <div className={styles.dividerLine} />
        <LineItemNotes />
      </div>
      
      <div className="bottom-controls-product-selection-sidebar">
        <div className="controls-block-grid">
          <div>Subtotal:</div>
          <div className="text-block-32">$ {Number(subtotal || 0).toFixed(2)}</div>
        </div>
        <button
          onClick={() => handleAddToCart(orderId, lineItemId, quantityRef.current?.value || '1', navigate, queryClient)}
          type="button"
          disabled={addToCart}
          className={`container-cart-button button-2 w-inline-block ${styles.cartButtonContainer}`}
        >
          <div className="div-block-186">
            <div className="icon right-margin w-embed">
              <svg xmlns="http://www.w3.org/2000/svg" fill="currentcolor" viewBox="0 0 256 256">
                <rect width="256" height="256" fill="none" />
                <path d="M223.87109,65.43115l-12.15722,66.86231A23.98711,23.98711,0,0,1,188.10156,152H72.13135l4.36377,24H184a24.01161,24.01161,0,1,1-22.624,16H102.624a24.00309,24.00309,0,1,1-40.85986-7.57666L34.05078,32H16a8,8,0,0,1,0-16H34.05078A15.9918,15.9918,0,0,1,49.793,29.13818L54.67676,56H216a8.00076,8.00076,0,0,1,7.87109,9.43115Z" />
              </svg>
            </div>
            <div>{addToCart ? (lineItemId ? 'Updating...' : 'Adding...') : (lineItemId ? 'Update' : 'Add to Cart')}</div>
          </div>
          <div className={`div-block-187 ${styles.cartCountDivider}`}>
            <div className={`div-block-188 ${styles.cartCountBadge}`}>
              <div>{cartCount}</div>
            </div>
          </div>
        </button>
        <Link to={`/lab-orders/${orderId}/summary`} className={`button-2 w-inline-block ${styles.reviewOrderButton}`}>
          <div className="icon right-margin w-embed">
            <svg xmlns="http://www.w3.org/2000/svg" fill="currentcolor" viewBox="0 0 256 256">
              <rect width="256" height="256" fill="none" />
              <path d="M247.31055,124.75061c-.35157-.79-8.81934-19.57617-27.65332-38.41113C194.57324,61.25256,162.87793,47.99182,128,47.99182S61.42676,61.25256,36.34277,86.33948c-18.834,18.835-27.30175,37.62109-27.65332,38.41113a8.00282,8.00282,0,0,0,0,6.49805c.35157.791,8.82032,19.57226,27.6543,38.40429C61.42773,194.734,93.12207,207.99182,128,207.99182S194.57227,194.734,219.65625,169.653c18.834-18.832,27.30273-37.61328,27.6543-38.40429A8.00282,8.00282,0,0,0,247.31055,124.75061ZM128,92.00061a36,36,0,1,1-36,36A36.04061,36.04061,0,0,1,128,92.00061Z" />
            </svg>
          </div>
          <div>Review Order</div>
        </Link>
      </div>
    </div>
  );
};

export default ProductSelectionSidebar;
